import { getCronoChromeExtensionId } from 'crono-fe-common/utils';
import { useQuery } from '@tanstack/react-query';

export enum CronoExtensionStatus {
  NOT_CHROME_BASED_BROWSER = 'notChromeBasedBrowser',
  NOT_INSTALLED = 'notInstalled',
  // this is a bit hacky: we use the same name as authError response that can come from extension
  INSTALLED_WITHOUT_LINKEDIN_COOKIE = 'authError',
  INSTALLED_WITH_LINKEDIN_COOKIE = 'installedWithLinkedinCookie',
}

type ExtensionStatusResult = {
  status: CronoExtensionStatus;
  hasSalesNavCookie?: boolean;
  hasExpiredSalesNavCookie?: boolean;
};

const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [100, 500, 1500];

// Returns null when the SW is asleep (lastError / undefined response) so the
// caller can retry; returns a definitive result otherwise.
function pingExtension(): Promise<ExtensionStatusResult | null> {
  return new Promise((resolve) => {
    try {
      // @ts-expect-error chrome is not defined
      chrome.runtime.sendMessage(
        getCronoChromeExtensionId(),
        { methodName: 'verifyLogin' },
        (callResult: {
          status?: unknown;
          result?: {
            hasLinkedinCookie?: boolean;
            hasSalesNavCookie?: boolean;
            hasExpiredSalesNavCookie?: boolean;
          };
        }) => {
          // Must read lastError synchronously inside the callback to suppress
          // Chrome's console warning when the MV3 service worker is waking up.
          // @ts-expect-error chrome is not defined
          const hadLastError = !!chrome.runtime.lastError;
          if (hadLastError || callResult === undefined) {
            resolve(null);
            return;
          }

          const status = callResult?.status;
          if (status === 'authError') {
            resolve({
              status: CronoExtensionStatus.INSTALLED_WITHOUT_LINKEDIN_COOKIE,
            });
          } else if (status !== 'callSuccess') {
            resolve({ status: CronoExtensionStatus.NOT_INSTALLED });
          } else {
            resolve({
              status: callResult?.result?.hasLinkedinCookie
                ? CronoExtensionStatus.INSTALLED_WITH_LINKEDIN_COOKIE
                : CronoExtensionStatus.INSTALLED_WITHOUT_LINKEDIN_COOKIE,
              hasSalesNavCookie: callResult?.result?.hasSalesNavCookie,
              hasExpiredSalesNavCookie:
                callResult?.result?.hasExpiredSalesNavCookie,
            });
          }
        },
      );
    } catch (_e) {
      resolve(null);
    }
  });
}

export function useGetExtensionStatus() {
  return useQuery({
    queryKey: ['getExtensionStatus'],
    // Prevent hitting the MV3 service worker on every window focus event.
    // 30s is long enough to avoid churn while still reflecting real changes.
    staleTime: 30 * 1000,

    queryFn: async () => {
      if (!('chrome' in window) || !window.chrome) {
        return { status: CronoExtensionStatus.NOT_CHROME_BASED_BROWSER };
      }

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (attempt > 0) {
          await new Promise<void>((r) =>
            setTimeout(r, RETRY_DELAYS_MS[attempt - 1]),
          );
        }
        const result = await pingExtension();
        if (result !== null) return result;
      }

      return { status: CronoExtensionStatus.NOT_INSTALLED };
    },
  });
}
